import { useEffect, useMemo, useState, useCallback } from "react";

import {
  AlertTriangle,
  CheckCircle,
  Clipboard,
  Target,
  Zap,
  Clock,
  Pencil,
  Info,
} from "lucide-react";

import AllAssignmentContainer from "./AllAssignmentContainer";
import ApprovalContainer from "./ApprovalContainer";
import UnassignedContainer from "./UnassignedContainer";
import Button from "../../Components/Button";
import DropdownSelector from "../../Components/DropdownSelector";
import RecommendationsLoader from "../../Components/RecommendationsLoader";
import RejectionModal from "../../Components/RejectionModal";
import SuccessNotification from "../../Components/SuccessNotification";
import TabsComponent from "../../Components/Tab";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchAssignments,
  fetchUnassignedCustomers,
  fetchPendingApprovals,
  approveOrRejectRequestAssessment,
  clearApproveRejectRequest,
} from "../../Store/Reducers/AssignmentSlice";
import { fetchActivationReady } from "../../Store/Reducers/OpsSlice";
import { formatCompactCurrency } from "../../Utils/Helper/formatCompactCurrency";
import { canApproveReject } from "../../Utils/Helper/Helper";
import {
  STORAGE_KEYS,
  getFromLocalStorage,
} from "../../Utils/Helper/LocalStorage";
import { toast } from "../../Utils/Helper/toast";
import ActivationsContainer from "../Activations";

import "./style.scss";

const AssignmentContainer = () => {
  const dispatch = useAppDispatch();
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);
  const canApprove = canApproveReject(loggedInUser?.role);
  const {
    assignments,
    prep_activated_assignments,
    assessment_assignments,
    total_assignments,
    pending_approvals,
    loading: assignmentsLoading,
    unassignedCustomers,
    unassignedCustomersLoading,
    unassignedCustomersError,
    pendingApprovals,
    approveRejectRequest,
    recommendations,
  } = useAppSelector(state => state.assignments);
  const { data: activationReadyData } = useAppSelector(
    state => state.ops.activationReady
  );
  const [activeTab, setActiveTab] = useState("all-assignments");
  const [activationSubTab, setActivationSubTab] = useState(
    "pending-activations"
  );
  const [selectedTimeFilter, setSelectedTimeFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [assignmentMode, setAssignmentMode] = useState<string>("auto");
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
  const [selectedRejectionRequest, setSelectedRejectionRequest] = useState<
    string | null
  >(null);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState({
    message: "",
    details: "",
  });
  const [lastActionType, setLastActionType] = useState<
    "approve" | "reject" | null
  >(null);
  const [processingAccountName, setProcessingAccountName] = useState<
    string | null
  >(null);
  const [processingAccountIndex, setProcessingAccountIndex] = useState(0);
  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [processingApprovalId, setProcessingApprovalId] = useState<
    string | null
  >(null);
  const [processingAction, setProcessingAction] = useState<
    "approve" | "reject" | "cancel" | null
  >(null);

  useEffect(() => {
    dispatch(fetchActivationReady());
  }, [dispatch]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleAssignmentModeChange = (mode: string) => {
    setAssignmentMode(mode);
    // Clear selections when switching modes
    setSelectedAccountIds([]);
  };

  const MAX_BULK_ACCOUNTS = 5;

  const handleToggleAccountSelection = (accountId: string) => {
    setSelectedAccountIds(prev => {
      if (prev.includes(accountId)) {
        return prev.filter(id => id !== accountId);
      }
      if (prev.length >= MAX_BULK_ACCOUNTS) {
        setTimeout(
          () =>
            toast.warning(
              "Maximum 5 accounts allowed. Deselect an account to select another."
            ),
          0
        );
        return prev;
      }
      return [...prev, accountId];
    });
  };

  const handleSelectAll = () => {
    const availableAccounts = unassignedAccountsData
      .filter(account => account.status !== "pending")
      .map(account => account.id);
    setSelectedAccountIds(availableAccounts.slice(0, MAX_BULK_ACCOUNTS));
  };

  const handleClearAll = () => {
    setSelectedAccountIds([]);
  };

  const isAccountSelected = (accountId: string) => {
    return selectedAccountIds.includes(accountId);
  };

  const getModeDisplay = () => {
    switch (assignmentMode) {
      case "auto":
        return "Auto";
      case "manual":
        return "Manual";
      default:
        return "Auto";
    }
  };

  const getCardButtonText = (account: any) => {
    if (account.status === "pending") {
      return (
        <>
          <Clock size={16} className="mr-2" />
          Pending Approval
        </>
      );
    }

    switch (assignmentMode) {
      case "auto":
        return isAccountSelected(account.id) ? (
          <>
            <Target size={16} className="mr-2" />
            Selected
          </>
        ) : (
          "Click to Select"
        );
      case "manual":
        return isAccountSelected(account.id) ? (
          <>
            <Target size={16} className="mr-2" />
            Selected
          </>
        ) : (
          <>
            <Pencil size={16} className="mr-2" />
            Manual Assign
          </>
        );
      default:
        return "Click to Select";
    }
  };

  const getCardButtonVariant = (account: any) => {
    if (account.status === "pending") {
      return "gray";
    }

    switch (assignmentMode) {
      case "auto":
        return isAccountSelected(account.id) ? "primary" : "secondary";
      case "manual":
        return isAccountSelected(account.id) ? "primary" : "secondary";
      default:
        return "secondary";
    }
  };

  // Transform API data to match component's expected structure
  const unassignedAccountsData = useMemo(() => {
    return unassignedCustomers.map(customer => {
      // Format revenue as currency
      const formattedRevenue = formatCompactCurrency(customer.revenue, {
        fallback: "N/A",
      });

      return {
        id: customer.customer_id,
        name: customer.name,
        industry: customer.industry || "N/A",
        location: customer.location || "-",
        tier: customer.tier,
        value: formattedRevenue,
        status: customer.assignment_status || "",
      };
    });
  }, [unassignedCustomers]);

  // Metric cards: All Assignments, Unassigned, Approvals, Activation (card-style with subtitle)
  const metricCards = useMemo(
    () => [
      {
        id: "all-assignments",
        label: "Total Assignments",
        count: total_assignments || 0,
        subtitle: "Accounts with executive assignments",
        icon: <Target size={22} />,
        iconBg: "bg-purple-100",
        iconColor: "text-cyan-600",
        gradient: "from-purple-50 to-purple-100/60",
      },
      {
        id: "Unassigned",
        label: "Unassigned",
        count: unassignedAccountsData.length,
        subtitle: "Accounts awaiting assignment",
        icon: <Clipboard size={22} />,
        iconBg: "bg-amber-100",
        iconColor: "text-amber-600",
        gradient: "from-amber-50 to-amber-100/60",
      },
      {
        id: "Approvals",
        label: "Pending Approvals",
        count: pending_approvals || 0,
        subtitle: "Awaiting review",
        icon: <CheckCircle size={22} />,
        iconBg: "bg-blue-100",
        iconColor: "text-rose-600",
        gradient: "from-blue-50 to-blue-100/60",
      },
      {
        id: "activation",
        label: "Activations",
        count: activationReadyData?.total ?? 0,
        subtitle: "Ongoing, incomplete, and completed",
        icon: <Zap size={22} />,
        iconBg: "bg-emerald-100",
        iconColor: "text-emerald-600",
        gradient: "from-emerald-50 to-emerald-100/60",
      },
    ],
    [
      total_assignments,
      unassignedAccountsData.length,
      pending_approvals,
      activationReadyData,
    ]
  );

  // Activation sub-tabs (shown when Activation tab is active)
  const activationSubTabs = useMemo(
    () => [
      {
        id: "pending-activations",
        label: "Pending Activations",
        icon: <Clock size={18} />,
        count: activationReadyData?.ongoing ?? 0,
        badgeType: "primary" as const,
      },
      {
        id: "incomplete-activations",
        label: "Ongoing/Incomplete Activations",
        icon: <AlertTriangle size={18} />,
        count: activationReadyData?.incomplete ?? 0,
        badgeType: "primary" as const,
      },
      {
        id: "completed-activations",
        label: "Completed Activations",
        icon: <CheckCircle size={18} />,
        count: activationReadyData?.complete ?? 0,
        badgeType: "primary" as const,
      },
    ],
    [activationReadyData]
  );

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  const timeFilterOptions = [
    { label: "All", value: "all" },
    { label: "7 days", value: "7-days" },
    { label: "30 days", value: "30-days" },
    { label: "90 days", value: "90-days" },
  ];

  // Handle refresh
  const handleRefreshApprovals = () => {
    refreshAllCounts();
  };

  // Handle approve/reject
  const handleApprove = async (requestId: string) => {
    setProcessingApprovalId(requestId);
    setProcessingAction("approve");
    setLastActionType("approve");
    try {
      await dispatch(
        approveOrRejectRequestAssessment({
          request_id: requestId,
          action: "approve",
          notes: "Approved assignment request",
        })
      );
    } finally {
      setProcessingApprovalId(null);
      setProcessingAction(null);
    }
  };

  const handleReject = (requestId: string) => {
    setSelectedRejectionRequest(requestId);
    setIsRejectionModalOpen(true);
  };

  const handleRejectionConfirm = async (reason: string) => {
    if (selectedRejectionRequest) {
      setProcessingApprovalId(selectedRejectionRequest);
      setProcessingAction("reject");
      setLastActionType("reject");
      try {
        await dispatch(
          approveOrRejectRequestAssessment({
            request_id: selectedRejectionRequest,
            action: "reject",
            notes: reason || "Rejected assignment request",
          })
        );
      } finally {
        setProcessingApprovalId(null);
        setProcessingAction(null);
        setIsRejectionModalOpen(false);
        setSelectedRejectionRequest(null);
      }
    }
  };

  // Refresh all metric counts (call after any action that changes assignments/approvals/activations)
  const refreshAllCounts = useCallback(() => {
    dispatch(fetchAssignments());
    dispatch(fetchUnassignedCustomers());
    dispatch(fetchPendingApprovals());
    dispatch(fetchActivationReady());
  }, [dispatch]);

  // Refresh approvals list after successful approve/reject
  useEffect(() => {
    if (approveRejectRequest.success && lastActionType) {
      refreshAllCounts();
      const actionText = lastActionType === "approve" ? "Approved" : "Rejected";
      const actionPast = lastActionType === "approve" ? "approved" : "rejected";
      setSuccessMessage({
        message: `Assignment Request ${actionText} Successfully`,
        details: `The assignment request has been ${actionPast}. The list will be updated automatically.`,
      });
      setShowSuccessNotification(true);
      setLastActionType(null);
      dispatch(clearApproveRejectRequest());
    }
  }, [approveRejectRequest.success, lastActionType, dispatch]);

  useEffect(() => {
    refreshAllCounts();
  }, [refreshAllCounts]);

  const assignmentsData = useMemo(() => {
    const allAssignments = [
      ...(assignments || []),
      ...(prep_activated_assignments || []),
      ...(assessment_assignments || []),
    ];

    return allAssignments.map(item => ({
      id: item.id,
      accountId:
        item.accountId || item.customer_id || item.account?.customer_id || null,
      accountName: item.account?.name || "-",
      accountSubtitle:
        `${(item.account as any)?.industry || ""} ${(item.account as any)?.country ? `• ${(item.account as any)?.country}` : ""}`.trim(),
      primaryExecutive: item.primary_executive?.full_name,
      primaryExecutiveId: item.primary_executive_id || item.executiveId || null,
      primaryExecutiveSubtitle: item.primary_executive?.position || "",
      secondaryExecutive: item.secondaryExecutive?.full_name,
      secondaryExecutiveId: item.secondaryExecutive?.user_id || null,
      secondaryExecutiveSubtitle: item.secondaryExecutive?.position || "",
      assignedOn: item.created_at
        ? new Date(item.created_at).toLocaleDateString(undefined, {
            month: "short",
            day: "2-digit",
            year: "numeric",
          })
        : "-",
      status: item.status,
      assignmentPriority: item.assignment_priority,
      relationshipType: item.relationship_type,
      batchInfo: item.batch_name ? `Batch: ${item.batch_name}` : "",
      batchName: item.batch_name || "",
      assignmentMode: item.type || "manual",
      notes: item.notes || "",
      actions: "Remove",
      // Store full assignment object for edit functionality
      fullAssignment: item,
    }));
  }, [assignments, prep_activated_assignments, assessment_assignments]);

  return (
    <div className="assignment-container">
      {/* Main Container */}
      <div className="assignment-container-content bg-white rounded-lg border border-gray-200 p-6">
        {/* Page header */}
        <div className="mb-4">
          <h1 className="text-xl font-semibold text-gray-900">
            Account Assignments
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage executive-account assignments with smart recommendations.
          </p>
        </div>

        {/* Metric Cards */}
        <div className="mb-0">
          <div className="metric-cards-container relative z-1">
            {/* Connector line for desktop cards (first to last) */}
            <div className="relative z-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-4">
              {metricCards.map(card => {
                const isActive = activeTab === card.id;
                return (
                  <button
                    key={card.id}
                    role="tab"
                    type="button"
                    aria-selected={isActive}
                    aria-controls={`panel-${card.id}`}
                    id={`tab-${card.id}`}
                    onClick={() => handleTabChange(card.id)}
                    className={`
                      assignment-metric-card group relative w-full flex items-center gap-3 p-4 text-left rounded-xl bg-white transition-all duration-200
                      ${
                        isActive
                          ? "assignment-metric-card--active border-2 border-[#974ae4] shadow-md z-1"
                          : "border border-gray-300 hover:border-gray-300 hover:shadow-sm"
                      }
                    `}
                  >
                    {/* Icon */}
                    <div
                      className={`metric-card-icon flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${card.iconBg} ${card.iconColor}`}
                    >
                      {card.icon}
                    </div>

                    {/* Content */}
                    <div className="min-w-0 flex-1">
                      <p
                        className={
                          "text-xs font-medium tracking-wide mb-0.5 text-gray-500 flex items-center gap-1"
                        }
                      >
                        <span>{card.label}</span>
                        <span className="relative inline-flex items-center">
                          <Info
                            size={12}
                            className="peer text-gray-500 cursor-help"
                            aria-label={`${card.label} info`}
                          />
                          <span className="pointer-events-none absolute left-1/2 top-full z-30 mt-1 -translate-x-1/2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-[11px] font-normal text-white opacity-0 shadow-md transition-opacity duration-150 peer-hover:opacity-100">
                            {card.subtitle}
                          </span>
                        </span>
                      </p>
                      <p className="text-2xl font-bold tabular-nums leading-none text-gray-900 mb-1">
                        {card.count}
                      </p>
                      <p className="text-xs text-gray-500 leading-tight">
                        {card.subtitle}
                      </p>
                    </div>

                    {/* Connector Arrow */}
                    {isActive && (
                      <div className="absolute -bottom-[10px] left-1/2 transform -translate-x-1/2 z-20 pointer-events-none">
                        <div className="w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[8px] border-t-[#974ae4]"></div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content Panel */}
        <div>
          {/* Tab content panel */}
          <div
            id={`panel-${activeTab}`}
            role="tabpanel"
            aria-labelledby={`tab-${activeTab}`}
          >
            {/* Activation sub-tabs and time filter - row below, only when Activation tab is active */}
            {activeTab === "activation" && (
              <div className="bg-white rounded-lg border px-3">
                <div className="flex items-center justify-between gap-4 mb-6 border-b">
                  <TabsComponent
                    tabs={activationSubTabs}
                    activeTab={activationSubTab}
                    onTabChange={setActivationSubTab}
                    variant="tab-underline"
                  />
                  <DropdownSelector
                    label="All"
                    selected={selectedTimeFilter}
                    options={timeFilterOptions}
                    onSelect={setSelectedTimeFilter}
                  />
                </div>
                <div>
                  <ActivationsContainer
                    activeFilter={activationSubTab}
                    timeFilter={selectedTimeFilter}
                  />
                </div>
              </div>
            )}

            {/* Tab Content */}
            {activeTab === "all-assignments" && (
              <div>
                {assignmentsLoading ? (
                  <div className="flex flex-col items-center justify-center min-h-[400px] py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                    <p className="text-gray-600 mt-2">Loading assignments...</p>
                  </div>
                ) : (
                  <AllAssignmentContainer
                    assignmentsData={assignmentsData}
                    searchQuery={searchQuery}
                    setSearchQuery={handleSearchChange}
                    onRefreshCounts={refreshAllCounts}
                  />
                )}
              </div>
            )}

            {activeTab === "Unassigned" && (
              <div>
                {unassignedCustomersLoading ? (
                  <div className="flex flex-col items-center justify-center min-h-[400px] py-12">
                    <div className="relative">
                      <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-[#974ae4]"></div>
                      <div className="absolute inset-0 animate-ping rounded-full h-12 w-12 border-2 border-[#974ae4]/20"></div>
                    </div>
                    <p className="text-gray-600 mt-4 font-medium">
                      Loading unassigned customers...
                    </p>
                  </div>
                ) : unassignedCustomersError ? (
                  <div className="flex flex-col items-center justify-center min-h-[400px] py-12 px-6">
                    <div className="flex flex-col items-center gap-4 max-w-md text-center">
                      <div className="flex items-center justify-center w-16 h-16 rounded-full bg-red-100">
                        <AlertTriangle className="w-8 h-8 text-red-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          Error Loading Customers
                        </h3>
                        <p className="text-sm text-gray-600 mb-6">
                          {unassignedCustomersError}
                        </p>
                      </div>
                      <Button
                        variant="primary"
                        onClick={() => refreshAllCounts()}
                        className="px-6 py-2.5"
                      >
                        Retry
                      </Button>
                    </div>
                  </div>
                ) : (
                  <UnassignedContainer
                    assignmentMode={assignmentMode}
                    setAssignmentMode={handleAssignmentModeChange}
                    unassignedAccountsData={unassignedAccountsData}
                    getModeDisplay={getModeDisplay}
                    getCardButtonText={getCardButtonText}
                    getCardButtonVariant={getCardButtonVariant}
                    selectedAccountIds={selectedAccountIds}
                    onToggleAccountSelection={handleToggleAccountSelection}
                    onSelectAll={handleSelectAll}
                    onClearAll={handleClearAll}
                    isAccountSelected={isAccountSelected}
                    onProcessingAccountChange={(accountName, accountIndex) => {
                      setProcessingAccountName(accountName);
                      setProcessingAccountIndex(accountIndex || 0);
                    }}
                    onManualModalStateChange={setIsManualModalOpen}
                    onBulkModalStateChange={setIsBulkModalOpen}
                    onRefreshCounts={refreshAllCounts}
                  />
                )}
              </div>
            )}

            {activeTab === "Approvals" && (
              <div>
                <ApprovalContainer
                  approvalsData={pendingApprovals.items}
                  onRefresh={handleRefreshApprovals}
                  onApprove={handleApprove}
                  onReject={handleReject}
                  loading={pendingApprovals.loading}
                  error={pendingApprovals.error}
                  approveRejectLoading={approveRejectRequest.loading}
                  approveRejectError={approveRejectRequest.error}
                  processingApprovalId={processingApprovalId}
                  processingAction={processingAction}
                  disableApproveReject={!canApprove}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Rejection Modal */}
      <RejectionModal
        isOpen={isRejectionModalOpen}
        onClose={() => {
          setIsRejectionModalOpen(false);
          setSelectedRejectionRequest(null);
        }}
        onConfirm={handleRejectionConfirm}
        title="Reject Assignment"
        description="Please provide a reason for rejecting this assignment. This will help improve future assignments."
        uploadName={
          selectedRejectionRequest
            ? pendingApprovals.items.find(
                a => a.id === selectedRejectionRequest
              )?.account_name || "Assignment Request"
            : "Assignment Request"
        }
      />

      {/* Success Notification */}
      <SuccessNotification
        isOpen={showSuccessNotification}
        onClose={() => setShowSuccessNotification(false)}
        message={successMessage.message}
        details={successMessage.details}
      />

      {/* Recommendations Loader - Only show for Unassigned tab */}
      {activeTab === "Unassigned" && (
        <RecommendationsLoader
          isOpen={
            recommendations.loading && !isManualModalOpen && !isBulkModalOpen
          }
          mode={selectedAccountIds.length > 1 ? "bulk" : "single"}
          assignmentMode={assignmentMode}
          currentAccountName={processingAccountName || undefined}
          currentAccountIndex={processingAccountIndex}
          totalAccounts={selectedAccountIds.length}
        />
      )}
    </div>
  );
};

export default AssignmentContainer;
